import { useEffect, useState, type FormEvent } from 'react';
import {
  listKillSwitches,
  createKillSwitch,
  deleteKillSwitch,
  type KillSwitch,
} from '../../services/events';

const SUGGEST_TYPES = [
  'DEBUGGER_SUSPECTED',
  'BREAKPOINT_SUSPECTED',
  'OPEN_HANDLE_SUSPECTED',
  'IAT_TAMPER_SUSPECTED',
  'SUSPICIOUS_MODULE_LOADED',
  'SUSPICIOUS_PARENT_PROCESS',
];

export function KillSwitches() {
  const [items, setItems] = useState<KillSwitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventType, setEventType] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const r = await listKillSwitches();
      setItems(r.data);
    } catch (e) {
      setError((e as Error).message || 'erro ao listar kill switches');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!eventType.trim()) return;
    setSubmitting(true);
    try {
      await createKillSwitch({ event_type: eventType.trim(), reason: reason.trim() });
      setEventType('');
      setReason('');
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'erro ao criar kill switch');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(uuid: string, type: string) {
    if (!confirm(`Re-habilitar ${type}?`)) return;
    try {
      await deleteKillSwitch(uuid);
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'erro ao deletar');
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-bold text-gray-200">Kill switches de eventos</h2>
        <p className="text-sm text-gray-500 mt-1">
          Sprint 4 / Ciclo 3b — desliga tipo de evento sem release de SDK (ADR-023).
          Detector continua rodando localmente; só não envia POST <code>/events</code>.
        </p>
      </header>

      <form
        onSubmit={handleCreate}
        className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 mb-6 flex gap-2 items-end"
      >
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Event type</label>
          <input
            type="text"
            list="event-types"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            placeholder="DEBUGGER_SUSPECTED"
            className="w-full bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-red-500 font-mono"
            required
          />
          <datalist id="event-types">
            {SUGGEST_TYPES.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div className="flex-[2]">
          <label className="block text-xs text-gray-500 mb-1">Motivo (audit)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="FP em massa após Win update KB123456"
            className="w-full bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-red-500"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded px-4 py-1.5"
        >
          {submitting ? 'Criando...' : 'Desativar'}
        </button>
      </form>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500">Nenhum kill switch ativo.</p>
      ) : (
        <div className="space-y-2">
          {items.map((k) => (
            <div
              key={k.id}
              className="bg-[#161a1f] border border-[#2a2f36] rounded p-3 flex items-center gap-3"
            >
              <code className="text-sm text-red-400 font-mono">{k.event_type}</code>
              <span className="text-sm text-gray-400 flex-1">{k.reason || '—'}</span>
              {k.disabled_until ? (
                <span className="text-xs text-gray-500">until {new Date(k.disabled_until).toLocaleString()}</span>
              ) : (
                <span className="text-xs text-amber-500">indefinido</span>
              )}
              <button
                type="button"
                onClick={() => handleDelete(k.id, k.event_type)}
                className="text-xs text-gray-400 hover:text-red-400 transition"
              >
                Re-habilitar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
